import { Directive, ElementRef, OnDestroy, OnInit, effect, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import { ThemeService } from '../services/theme.service';
import { TurnstileVerificationService } from '../services/turnstile-verification.service';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

/**
 * Decorator: encapsula la integracion imperativa con el script de terceros
 * de Cloudflare (window.turnstile.render, con render=explicit en
 * src/index.html) detras de un atributo -- mismo rol que [demoClosePanel] ya
 * cumple para el menu movil. TurnstileGateComponent no sabe nada de
 * window.turnstile, solo pone este atributo en un <div>.
 *
 * El script de Cloudflare tiene `defer`, asi que window.turnstile puede no
 * existir todavia cuando este directive corre -- se espera con un poll de
 * 100ms, mismo mecanismo que el TurnstileGate de reinvent-x-app (React).
 */
@Directive({
  selector: '[demoTurnstileWidget]',
  standalone: true,
})
export class TurnstileWidgetDirective implements OnInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly theme = inject(ThemeService);
  private readonly turnstile = inject(TurnstileVerificationService);

  private widgetId: string | undefined;
  private pollInterval: ReturnType<typeof setInterval> | undefined;
  private destroyed = false;

  constructor() {
    // Re-renderiza el widget si el visitante cambia de tema mientras el
    // gate sigue visible -- mismo comportamiento que la dependencia [theme]
    // del useEffect en el TurnstileGate de reinvent-x-app (React).
    effect(() => {
      this.theme.theme();
      if (this.widgetId !== undefined) {
        this.renderWidget();
      }
    });
  }

  ngOnInit(): void {
    this.waitForTurnstile();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pollInterval !== undefined) {
      clearInterval(this.pollInterval);
    }
    if (this.widgetId !== undefined && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  }

  private waitForTurnstile(): void {
    if (window.turnstile) {
      this.renderWidget();
      return;
    }
    this.pollInterval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(this.pollInterval);
        this.renderWidget();
      }
    }, 100);
  }

  private renderWidget(): void {
    if (this.destroyed || !window.turnstile) {
      return;
    }
    if (this.widgetId !== undefined) {
      window.turnstile.remove(this.widgetId);
    }
    this.host.nativeElement.innerHTML = '';
    this.widgetId = window.turnstile.render(this.host.nativeElement, {
      sitekey: environment.turnstileSiteKey,
      theme: this.theme.theme(),
      callback: (token) => this.turnstile.verifyToken(token),
      'error-callback': () => this.turnstile.reportClientError(),
      'expired-callback': () => this.turnstile.reset(),
    });
  }
}
